import { i18n } from '@b2ag/locale'

const FORMAT_MAP: Record<string, Intl.DateTimeFormatOptions> = {
  'dd/LL/yyyy': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'dd/MM/yyyy': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'dd LLLL yyyy': { day: '2-digit', month: 'long', year: 'numeric' },
  'd LLL yyyy': { day: 'numeric', month: 'short', year: 'numeric' },
  'dd MMMM': { day: '2-digit', month: 'long' },
}

function formatWithIntl(date: Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, options).format(date)
}

function formatDateTimeShortFr(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} à ${hours}:${minutes}`
}

export function formatDate(date: Date | undefined, format?: string): string | undefined
export function formatDate(date: Date | undefined, format?: Intl.DateTimeFormatOptions): string | undefined
export function formatDate(
  date: Date | undefined,
  format: string | Intl.DateTimeFormatOptions = 'dd/LL/yyyy',
): string | undefined {
  if (!date || isNaN(date.getTime())) return undefined
  const locale = i18n.locale || 'fr'

  if (typeof format !== 'string') {
    return formatWithIntl(date, locale, format)
  }

  if (format === 'dd/MM/yy à HH:mm') {
    return formatDateTimeShortFr(date)
  }

  const intlOptions = FORMAT_MAP[format]
  if (!intlOptions) {
    throw new Error(`Unsupported date format: "${format}". Add it to FORMAT_MAP in @b2ag/l10n.`)
  }
  return formatWithIntl(date, locale, intlOptions)
}

export function formatDateInterval(startDate: Date | undefined, endDate: Date | undefined, format = 'dd/LL/yyyy') {
  if (!startDate && !endDate) return undefined
  if (startDate?.toString() === endDate?.toString()) return `le ${formatDate(startDate, format)}`
  if (!startDate) return `jusqu\u2019au ${formatDate(endDate, format)}`
  if (!endDate) return `à partir du ${formatDate(startDate, format)}`
  return `du ${formatDate(startDate, format)} au ${formatDate(endDate, format)}`
}

/**
 * To be used as second argument for `formatDate`
 *
 * when local is `fr-FR` : `formatDate(new Date(), localizedShortFormat)` => `'18/08/2025'`
 *
 * when local is `en-US` : `formatDate(new Date(), localizedShortFormat)` => `'08/18/2025'`
 */
export const localizedShortDateFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}

export function parseDDMMYYYY(str: string): Date | null {
  const parts = str.split('/')
  if (parts.length !== 3) return null
  const [day, month, year] = parts as [string, string, string]
  const d = new Date(+year, +month - 1, +day)
  if (isNaN(d.getTime())) return null
  return d
}

export function toISODateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
